'use client'

/**
 * Combobox / MultiCombobox — doorzoekbare dropdown op cmdk + Radix Popover.
 * cmdk levert toetsenbordnavigatie (pijl/Enter/Esc/type-to-jump), a11y en
 * focus-management; Radix Popover levert portal + positionering (geen
 * clipping door overflow-hidden). Filtering doen we zelf via de geteste
 * `filterOptions` (shouldFilter={false}) zodat we op label + hint matchen.
 */

import * as React from 'react'
import { Command } from 'cmdk'
import { Check, ChevronsUpDown, Plus, Search, X } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from './popover'
import { cn } from '../lib/utils'
import {
  filterOptions,
  groupOptions,
  toggleValue,
  type ComboboxOption,
} from '../lib/combobox-filter'

export type { ComboboxOption }

const TRIGGER_CLASS =
  'flex w-full items-center justify-between gap-2 rounded-md border border-[#CED0C8] bg-white px-2 py-1.5 text-left text-sm text-[#1A1A18] focus:outline-none focus:ring-2 focus:ring-[#CCFA78] disabled:bg-[#F0F1EC] disabled:text-[#9CA3AF]'

function SearchHeader({
  query,
  setQuery,
  placeholder,
  right,
}: {
  query: string
  setQuery: (v: string) => void
  placeholder?: string
  right?: React.ReactNode
}) {
  return (
    <div className="flex items-center gap-1.5 border-b border-[#CED0C8] bg-[#F8F9F5] px-2 py-1.5">
      <Search className="h-3.5 w-3.5 shrink-0 text-[#5C5C58]" aria-hidden="true" />
      <Command.Input
        value={query}
        onValueChange={setQuery}
        placeholder={placeholder ?? 'Zoeken…'}
        className="flex-1 bg-transparent text-sm text-[#1A1A18] placeholder-[#9CA3AF] focus:outline-none"
      />
      {right}
    </div>
  )
}

// ── Single ────────────────────────────────────────────────────────────────
export interface ComboboxProps {
  options: ReadonlyArray<ComboboxOption>
  value: string | null
  onChange: (value: string | null) => void
  placeholder?: string
  searchPlaceholder?: string
  emptyText?: string
  disabled?: boolean
  /** Klik op de geselecteerde optie deselecteert hem. Default false. */
  clearable?: boolean
  /** Aangeroepen als de zoekterm wijzigt — voor serverside zoeken (bv. klanten). */
  onSearchChange?: (query: string) => void
  /**
   * Serverside-modus is bezig met laden (debounce/fetch). Toont "Zoeken…" i.p.v.
   * "Geen resultaten" zolang er nog geen opties zijn. Alleen zinvol samen met
   * `onSearchChange`.
   */
  loading?: boolean
  /**
   * Vaste actie onderaan de lijst (bv. "+ Nieuw type aanmaken…"). Sluit de
   * dropdown en roept `onSelect` aan — de caller opent daar zijn modal.
   * Blijft zichtbaar bij "Geen resultaten" zodat aanmaken altijd kan.
   */
  footerAction?: { label: string; onSelect: () => void }
  id?: string
  className?: string
  contentClassName?: string
}

export function Combobox({
  options,
  value,
  onChange,
  placeholder = 'Kies…',
  searchPlaceholder,
  emptyText = 'Geen resultaten',
  disabled = false,
  clearable = false,
  onSearchChange,
  loading = false,
  footerAction,
  id,
  className,
  contentClassName,
}: ComboboxProps) {
  const [open, setOpen] = React.useState(false)
  const [query, setQuery] = React.useState('')
  const handleSearch = (v: string) => {
    setQuery(v)
    onSearchChange?.(v)
  }
  // FO-125 — clear -> reselect deterministisch. Reset de interne zoekterm zodra
  // de externe `value` wijzigt (selecteren, wissen via de X, of programmatisch),
  // zodat er na een wis-actie geen oude query-resttoestand achterblijft die de
  // volgende selectie hindert. Niet-destructief: een selectie sluit de dropdown
  // (`setOpen(false)`) die de query toch al wist; dit borgt hetzelfde voor de
  // wis-X (die buiten de Popover staat) en voor externe waarde-wijzigingen.
  React.useEffect(() => {
    setQuery('')
  }, [value])
  const selected = options.find((o) => o.value === value) ?? null
  // Serverside-modus (onSearchChange gezet): de opties zijn al door de server op
  // `query` gefilterd — niet nóg eens client-side filteren (dat zou tijdens de
  // debounce de zojuist opgehaalde matches kunnen wegfilteren als ze niet als
  // substring in label/hint zitten, bv. match op klantnummer/adres). Client-side
  // modus (geen onSearchChange) houdt de geteste filterOptions-tak.
  const filtered = React.useMemo(
    () => (onSearchChange ? [...options] : filterOptions(options, query)),
    [onSearchChange, options, query],
  )
  const grouped = React.useMemo(() => groupOptions(filtered), [filtered])
  // #79 — toon een wis-X op de trigger zodra er iets gekozen is en de caller
  // `clearable` heeft gezet. Lost het "filter niet te wissen"-knelpunt in één
  // keer op voor alle enkelvoudige pickers (verlof/verzuim, etc.). De X is een
  // sibling van de PopoverTrigger (geen geneste button) en stopt propagatie
  // zodat hij de dropdown niet opent.
  const showClear = clearable && value != null && !disabled

  return (
    <div className={cn('relative', className)}>
      <Popover
        open={open}
        onOpenChange={(o) => {
          setOpen(o)
          if (!o) setQuery('')
        }}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            id={id}
            disabled={disabled}
            role="combobox"
            aria-expanded={open}
            className={cn(TRIGGER_CLASS, showClear && 'pr-12')}
          >
            <span className={selected ? '' : 'text-[#9CA3AF]'}>
              {selected ? selected.label : placeholder}
            </span>
            <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-[#5C5C58]" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className={cn('w-[--radix-popover-trigger-width] p-0', contentClassName)}
        >
          <Command shouldFilter={false}>
            <SearchHeader query={query} setQuery={handleSearch} placeholder={searchPlaceholder} />
            <Command.List className="max-h-72 overflow-y-auto py-1">
              {filtered.length === 0 ? (
                <div className="px-3 py-2 text-xs text-[#5C5C58]">
                  {loading ? 'Zoeken…' : emptyText}
                </div>
              ) : (
                grouped.map(({ group, items }) => (
                  <Command.Group
                    key={group ?? '_'}
                    heading={group}
                    className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-1 [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wide [&_[cmdk-group-heading]]:text-[#5C5C58]"
                  >
                    {items.map((o) => {
                      const isSel = o.value === value
                      return (
                        <Command.Item
                          key={o.value}
                          value={o.value}
                          disabled={o.disabled}
                          title={o.title}
                          onSelect={() => {
                            if (o.disabled) return
                            onChange(isSel && clearable ? null : o.value)
                            setOpen(false)
                          }}
                          className={cn(
                            'flex items-center gap-2 px-3 py-1.5 text-sm text-[#1A1A18] data-[selected=true]:bg-[#F0F1EC]',
                            o.disabled
                              ? 'cursor-not-allowed text-[#9CA3AF] data-[selected=true]:bg-transparent'
                              : 'cursor-pointer',
                          )}
                        >
                          <Check
                            className={cn(
                              'h-4 w-4 shrink-0',
                              isSel ? 'text-[#1A1A18] opacity-100' : 'opacity-0',
                            )}
                            aria-hidden="true"
                          />
                          <span className="flex-1">
                            {o.label}
                            {o.hint && (
                              <span className="ml-1 text-xs text-[#5C5C58]">({o.hint})</span>
                            )}
                          </span>
                          {o.suffix && (
                            <span className="shrink-0 rounded-full border border-[#CED0C8] bg-[#F0F1EC] px-1.5 py-0.5 text-[10px] font-semibold text-[#5C5C58]">
                              {o.suffix}
                            </span>
                          )}
                        </Command.Item>
                      )
                    })}
                  </Command.Group>
                ))
              )}
            </Command.List>
            {footerAction && (
              <button
                type="button"
                onClick={() => {
                  setOpen(false)
                  setQuery('')
                  footerAction.onSelect()
                }}
                className="flex w-full items-center gap-2 border-t border-[#CED0C8] bg-[#F8F9F5] px-3 py-2 text-sm font-medium text-[#1A1A18] hover:bg-[#F0F1EC]"
              >
                <Plus className="h-4 w-4 shrink-0 text-[#5C5C58]" aria-hidden="true" />
                {footerAction.label}
              </button>
            )}
          </Command>
        </PopoverContent>
      </Popover>
      {showClear && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            onChange(null)
          }}
          aria-label="Selectie wissen"
          className="absolute right-7 top-1/2 -translate-y-1/2 rounded p-0.5 text-[#5C5C58] hover:bg-[#F0F1EC] hover:text-[#1A1A18] focus:outline-none focus:ring-2 focus:ring-[#CCFA78]"
        >
          <X className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      )}
    </div>
  )
}

// ── Multi ─────────────────────────────────────────────────────────────────
export interface MultiComboboxProps {
  options: ReadonlyArray<ComboboxOption>
  value: ReadonlyArray<string>
  onChange: (next: string[]) => void
  placeholder?: string
  searchPlaceholder?: string
  emptyText?: string
  clearLabel?: string
  disabled?: boolean
  /** Toon chip-rij met selectie onder de trigger. Default true. */
  showChips?: boolean
  /** Aangeroepen als de zoekterm wijzigt — voor serverside zoeken (bv. klanten). */
  onSearchChange?: (query: string) => void
  /**
   * Serverside-modus is bezig met laden (debounce/fetch). Toont "Zoeken…" i.p.v.
   * "Geen resultaten" zolang er nog geen opties zijn. Alleen zinvol samen met
   * `onSearchChange`.
   */
  loading?: boolean
  id?: string
  className?: string
  contentClassName?: string
}

export function MultiCombobox({
  options,
  value,
  onChange,
  placeholder = 'Alle',
  searchPlaceholder,
  emptyText = 'Geen resultaten',
  clearLabel = 'Alles wissen',
  disabled = false,
  showChips = true,
  onSearchChange,
  loading = false,
  id,
  className,
  contentClassName,
}: MultiComboboxProps) {
  const [open, setOpen] = React.useState(false)
  const [query, setQuery] = React.useState('')
  const handleSearch = (v: string) => {
    setQuery(v)
    onSearchChange?.(v)
  }
  const selectedSet = React.useMemo(() => new Set(value), [value])
  // Serverside-modus (onSearchChange gezet): de opties zijn al door de server op
  // `query` gefilterd — niet nóg eens client-side filteren (dat zou tijdens de
  // debounce de zojuist opgehaalde matches kunnen wegfilteren als ze niet als
  // substring in label/hint zitten, bv. match op klantnummer/adres). Client-side
  // modus (geen onSearchChange) houdt de geteste filterOptions-tak.
  const filtered = React.useMemo(
    () => (onSearchChange ? [...options] : filterOptions(options, query)),
    [onSearchChange, options, query],
  )
  const grouped = React.useMemo(() => groupOptions(filtered), [filtered])

  const preview = React.useMemo(() => {
    if (value.length === 0) return placeholder
    const labels = options.filter((o) => selectedSet.has(o.value)).map((o) => o.label)
    if (labels.length <= 2) return labels.join(', ')
    return `${labels.slice(0, 2).join(', ')} +${labels.length - 2}`
  }, [value, options, selectedSet, placeholder])

  const toggle = (v: string) => {
    onChange(toggleValue(value, v))
    // Spec 11 §4 — wis de zoektekst na selectie zodat de volgende naam direct
    // getypt kan worden. cmdk houdt de focus op het zoekveld (Command.Input),
    // dus de cursor blijft staan zonder handmatige ref-focus.
    setQuery('')
  }

  return (
    <div className={className}>
      <Popover
        open={open}
        onOpenChange={(o) => {
          setOpen(o)
          if (!o) setQuery('')
        }}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            id={id}
            disabled={disabled}
            aria-haspopup="listbox"
            aria-expanded={open}
            className={TRIGGER_CLASS}
          >
            <span className={value.length === 0 ? 'text-[#9CA3AF]' : ''}>{preview}</span>
            <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-[#5C5C58]" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className={cn('w-[--radix-popover-trigger-width] p-0', contentClassName)}
        >
          <Command shouldFilter={false}>
            <SearchHeader
              query={query}
              setQuery={handleSearch}
              // Spec 11 §4 — toon het aantal geselecteerde items in de placeholder.
              placeholder={
                value.length > 0
                  ? `${value.length} geselecteerd - typ om meer toe te voegen…`
                  : searchPlaceholder
              }
              right={
                value.length > 0 ? (
                  <button
                    type="button"
                    onClick={() => onChange([])}
                    className="rounded text-xs font-medium text-[#5C5C58] hover:text-[#1A1A18]"
                  >
                    {clearLabel}
                  </button>
                ) : null
              }
            />
            <Command.List className="max-h-72 overflow-y-auto py-1">
              {filtered.length === 0 ? (
                <div className="px-3 py-2 text-xs text-[#5C5C58]">
                  {loading ? 'Zoeken…' : emptyText}
                </div>
              ) : (
                grouped.map(({ group, items }) => (
                  <Command.Group
                    key={group ?? '_'}
                    heading={group}
                    className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-1 [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wide [&_[cmdk-group-heading]]:text-[#5C5C58]"
                  >
                    {items.map((o) => {
                      const sel = selectedSet.has(o.value)
                      return (
                        <Command.Item
                          key={o.value}
                          value={o.value}
                          title={o.title}
                          onSelect={() => toggle(o.value)}
                          className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm text-[#1A1A18] data-[selected=true]:bg-[#F0F1EC]"
                        >
                          <span
                            className={
                              sel
                                ? 'flex h-4 w-4 shrink-0 items-center justify-center rounded border-2 border-[#CCFA78] bg-[#CCFA78]'
                                : 'flex h-4 w-4 shrink-0 items-center justify-center rounded border-2 border-[#CED0C8]'
                            }
                          >
                            {sel && <Check className="h-3 w-3 text-[#1A1A18]" aria-hidden="true" />}
                          </span>
                          <span className="flex-1">
                            {o.label}
                            {o.hint && (
                              <span className="ml-1 text-xs text-[#5C5C58]">({o.hint})</span>
                            )}
                          </span>
                          {o.suffix && (
                            <span className="shrink-0 rounded-full border border-[#CED0C8] bg-[#F0F1EC] px-1.5 py-0.5 text-[10px] font-semibold text-[#5C5C58]">
                              {o.suffix}
                            </span>
                          )}
                        </Command.Item>
                      )
                    })}
                  </Command.Group>
                ))
              )}
            </Command.List>
          </Command>
        </PopoverContent>
      </Popover>

      {showChips && value.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1">
          {options
            .filter((o) => selectedSet.has(o.value))
            .map((o) => (
              <span
                key={o.value}
                className="inline-flex items-center gap-1 rounded-full bg-[#CCFA78] px-2 py-0.5 text-xs font-medium text-[#1A1A18]"
              >
                {o.label}
                <button
                  type="button"
                  onClick={() => toggle(o.value)}
                  aria-label={`${o.label} verwijderen`}
                  className="text-[#1A1A18] hover:opacity-70"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
        </div>
      )}
    </div>
  )
}
